import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';
import { Search, ChevronDown, X, Menu, Bookmark, MoreHorizontal, Sun, Moon, User, LogOut, Shield, Pencil } from 'lucide-react';
import BreakingTicker from '@/components/BreakingTicker';
import CookieBanner from '@/components/CookieBanner';
import AccessibilityWidget, { useA11yInit } from '@/components/AccessibilityWidget';
import BugReportButton from '@/components/BugReportButton';
import EminaAsistent from '@/components/EminaAsistent';
import FooterPartners from '@/components/FooterPartners';
import FooterAttributionBar from '@/components/FooterAttributionBar';
import PushSubscribePrompt from '@/components/PushSubscribePrompt';
import ScrollHeatmapCollector from '@/components/ScrollHeatmapCollector';
import InterestsOnboardingModal from '@/components/InterestsOnboardingModal';
import BackToTop from '@/components/BackToTop';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin quick-edit shortcut — shown next to the theme switcher for logged-in
 * admins. Detects the current route and opens the matching admin edit page
 * (article edit, category edit, or the admin dashboard as fallback).
 */
function AdminEditShortcut() {
  const navigate = useNavigate();
  const [path] = useState(() => window.location.pathname);

  async function openEdit() {
    try {
      const p = window.location.pathname;
      const match = p.match(/^\/clanak\/([^/?#]+)/);
      if (match) {
        const slug = match[1];
        const r = await fetch(`${API}/public/articles/${encodeURIComponent(slug)}`);
        if (r.ok) {
          const d = await r.json();
          const id = d?.article?.id;
          if (id) { navigate(`/admin/articles/${id}/edit`); return; }
        }
      }
      if (p.startsWith('/kategorija/')) { navigate('/admin/categories'); return; }
      if (p.startsWith('/oglasnik')) { navigate('/admin/classifieds'); return; }
      if (p.startsWith('/posao')) { navigate('/admin/jobs'); return; }
      if (p.startsWith('/oglasavanje')) { navigate('/admin/ad-discounts'); return; }
      if (p.startsWith('/sponzorirani-clanak')) { navigate('/admin/sponsored-settings'); return; }
      navigate('/admin');
    } catch { navigate('/admin'); }
  }

  return (
    <button onClick={openEdit} title="Uredi ovu stranicu (admin)" aria-label="Admin Edit"
      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
      data-testid="admin-edit-shortcut" data-current-path={path}>
      <Pencil size={17} />
    </button>
  );
}

function NavDropdown({ item, onClose }) {
  const { t } = useTranslation();
  return (
    <div className="absolute top-full left-0 mt-0 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-b-lg shadow-lg py-1 min-w-[220px] z-50">
      {item.dropdown.map(sub => (
        <Link key={sub.to} to={sub.to} onClick={onClose} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-red-600 transition-colors">
          {sub.labelKey ? t(sub.labelKey) : sub.label}
        </Link>
      ))}
    </div>
  );
}

const LANGS = [
  { code: 'hr', label: 'Hrvatski', flag: '🇭🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰' },
  { code: 'hu', label: 'Magyar', flag: '🇭🇺' },
];

const NAV_ITEMS = [
  { key: 'home', labelKey: 'nav.home', to: '/' },
  { key: 'currentNews', labelKey: 'nav.currentNews', to: '/kategorija/hrvatska-vijesti' },
  { key: 'news', labelKey: 'nav.news', dropdown: [
    { labelKey: 'nav.sub_hrvatska', to: '/kategorija/hrvatska-vijesti' },
    { labelKey: 'nav.sub_varazdinske', to: '/kategorija/varazdinske-vijesti' },
    { labelKey: 'nav.sub_crna', to: '/kategorija/crna-kronika' },
  ]},
  { key: 'events', labelKey: 'nav.events', dropdown: [
    { labelKey: 'nav.sub_lokalna', to: '/kategorija/lokalna-dogadanja-u-medimurju' },
    { labelKey: 'nav.sub_kultura', to: '/kategorija/kultura' },
    { labelKey: 'nav.sub_sport', to: '/kategorija/sport' },
  ]},
  { key: 'contact', labelKey: 'nav.contact', to: '/kontakt' },
  { key: 'oglasnik', labelKey: 'nav.oglasnik', to: '/oglasnik' },
  { key: 'posao', labelKey: 'nav.posao', to: '/posao' },
  { key: 'submit_news', labelKey: 'nav.submit_news', to: '/predaj-vijest' },
  { key: 'advertising', labelKey: 'nav.advertising', to: '/oglasavanje' },
];

const PHASE59_MORE_MENU = {
  "Korisno u Međimurju": [
    { label: "Turizam u Međimurju", to: "/kategorija/turizam-u-medimurju" },
    { label: "Gastronomija", to: "/kategorija/gastronomija" },
    { label: "Obrazovanje", to: "/kategorija/obrazovanje" },
    { label: "Zdravstvo", to: "/kategorija/zdravstvo" },
  ],
  "Vijesti po gradovima": [
    { label: "Čakovec", to: "/kategorija/vijesti-cakovec" },
    { label: "Prelog", to: "/kategorija/vijesti-prelog" },
    { label: "Mursko Središće", to: "/kategorija/vijesti-mursko-srediste" },
  ],
  "Teme": [
    { label: "Svijet", to: "/kategorija/vijesti-svijet" },
    { label: "Business", to: "/kategorija/business-vijesti" },
    { label: "EU projekti", to: "/kategorija/eu-projekti-i-suradnje" },
    { label: "Gospodarstvo i okoliš", to: "/kategorija/gospodarstvo-i-okolis" },
    { label: "Kripto", to: "/kategorija/vijesti-iz-kriptovaluta" },
    { label: "Edukacija i karijera", to: "/kategorija/edukacija-posao-karijera" },
    { label: "Istraživanja i kolumne", to: "/kategorija/istrazivanja-misljenja-kolumne" },
  ],
  "Praktično": [
    { label: "Promet", to: "/kategorija/medjimurje-promet" },
    { label: "Vrijeme", to: "/vrijeme" },
    { label: "Mapa Međimurja", to: "/mapa" },
    { label: "Multimedija", to: "/kategorija/multimedija" },
    { label: "Korisno", to: "/kategorija/korisno-u-medjimurju" },
  ],
  "Servisi": [
    { label: "Oglasnik", to: "/oglasnik" },
    { label: "Posao", to: "/posao" },
    { label: "Kvizovi", to: "/kvizovi" },
    { label: "Kripto napojnice", to: "/kripto" },
    { label: "Predaj vijest", to: "/predaj-vijest" },
    { label: "Oglašavanje", to: "/oglasavanje" },
  ],
};

function DesktopNav() {
  const { t } = useTranslation();
  const [openDrop, setOpenDrop] = useState(null);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const containerRef = useRef(null);
  const overflowRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let raf = 0;
    const measure = () => {
      const children = Array.from(el.querySelectorAll('[data-nav-slot]'));
      if (!children.length) {
        // Fallback until the measurement row has rendered.
        // With px-3 padding + text-[13px] a Croatian nav label averages ~110px.
        const perItem = 115;
        const availW = Math.max(0, el.offsetWidth - 60);
        const fits = Math.floor(availW / perItem);
        setHiddenItems(fits >= NAV_ITEMS.length ? [] : NAV_ITEMS.slice(fits));
        return;
      }
      const widths = children.map(c => (c.offsetWidth || 110) + 4 /* gap */);
      const availW = Math.max(0, el.offsetWidth - 60);
      let used = 0;
      let cutIdx = NAV_ITEMS.length;
      for (let i = 0; i < widths.length; i++) {
        if (used + widths[i] > availW) { cutIdx = i; break; }
        used += widths[i];
      }
      setHiddenItems(cutIdx < NAV_ITEMS.length ? NAV_ITEMS.slice(cutIdx) : []);
    };
    // Defer first measurement until fonts have loaded — otherwise widths are
    // underestimated on first paint and everything "fits" then reflows.
    const kick = () => { raf = requestAnimationFrame(measure); };
    kick();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(kick).catch(() => {});
    }
    const ro = new ResizeObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); });
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!overflowOpen) return;
    const onClick = (e) => { if (overflowRef.current && !overflowRef.current.contains(e.target)) setOverflowOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [overflowOpen]);

  const visible = hiddenItems.length ? NAV_ITEMS.slice(0, NAV_ITEMS.length - hiddenItems.length) : NAV_ITEMS;

  return (
    <div ref={containerRef} className="hidden xl:flex items-center gap-1 flex-1 min-w-0 relative justify-start" data-testid="desktop-nav">
      {/* Render ALL items once (invisible duplicate) so we can measure actual
         widths, then re-render just `visible` on top. Absolutely positioned
         so it contributes zero layout width; opacity-0 + pointer-events-none
         but NO `visibility:hidden` because some browsers skip measurement on
         visibility-hidden subtrees. */}
      <div aria-hidden="true" className="absolute top-0 left-0 opacity-0 pointer-events-none flex items-center gap-1 whitespace-nowrap">
        {NAV_ITEMS.map(item => (
          <span key={`m-${item.key}`} data-nav-slot="1" className="px-3 py-2 text-[13px] font-bold tracking-tight whitespace-nowrap">
            {t(item.labelKey)}{item.dropdown ? ' ▾' : ''}
          </span>
        ))}
      </div>
      {visible.map(item => (
        <div key={item.key} className="relative" onMouseEnter={() => item.dropdown && setOpenDrop(item.key)} onMouseLeave={() => setOpenDrop(null)}>
          {item.to ? (
            <Link to={item.to} className="px-3 py-2 text-[13px] font-bold text-gray-700 dark:text-gray-200 hover:text-red-600 transition-colors tracking-tight whitespace-nowrap" data-testid={`nav-${item.key}`}>
              {t(item.labelKey)}
            </Link>
          ) : (
            <button className="flex items-center gap-0.5 px-3 py-2 text-[13px] font-bold text-gray-700 dark:text-gray-200 hover:text-red-600 transition-colors tracking-tight whitespace-nowrap" data-testid={`nav-${item.key}`}>
              {t(item.labelKey)} <ChevronDown size={12} />
            </button>
          )}
          {item.dropdown && openDrop === item.key && <NavDropdown item={item} onClose={() => setOpenDrop(null)} />}
        </div>
      ))}
      {/* Always-visible "More menu" containing 14 Phase 5.9 categories */}
      <div className="relative ml-auto" ref={overflowRef} data-testid="nav-overflow">
        <button onClick={() => setOverflowOpen(o => !o)} aria-label="Više kategorija"
          className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400" data-testid="nav-overflow-btn">
          <MoreHorizontal size={18} />
        </button>
        {overflowOpen && (
          <div className="absolute right-0 top-full mt-1 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl py-2 min-w-[320px] max-w-[420px] z-50" data-testid="more-menu">
            {/* Overflow nav items at top (if any) */}
            {hiddenItems.length > 0 && (
              <div className="pb-1 mb-1 border-b border-gray-100 dark:border-gray-800">
                {hiddenItems.map(item => item.to ? (
                  <Link key={item.key} to={item.to} onClick={() => setOverflowOpen(false)} className="block px-4 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">{t(item.labelKey)}</Link>
                ) : (
                  item.dropdown.map(sub => <Link key={sub.to} to={sub.to} onClick={() => setOverflowOpen(false)} className="block px-4 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">{t(sub.labelKey)}</Link>)
                ))}
              </div>
            )}
            {/* Grouped Phase 5.9 categories */}
            {Object.entries(PHASE59_MORE_MENU).map(([group, items]) => (
              <div key={group} className="py-1">
                <p className="px-4 pt-1 pb-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">{group}</p>
                {items.map(it => (
                  <Link key={it.to} to={it.to} onClick={() => setOverflowOpen(false)}
                    className="block px-4 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                    data-testid={`more-item-${it.to.split('/').pop()}`}>
                    {it.label}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NewsletterWidget() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [sending, setSending] = useState(false);
  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/public/newsletter/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), locale: i18n.language || 'hr' }),
      });
      setStatus(r.ok ? 'success' : 'error');
      if (r.ok) setEmail('');
    } catch { setStatus('error'); }
    finally { setSending(false); }
  }
  return (
    <div className="bg-slate-900 border-b border-slate-800" data-testid="newsletter-widget">
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <h3 className="text-lg font-bold text-white mb-2">{t('newsletter.title')}</h3>
        <p className="text-sm text-gray-400 mb-4">{t('newsletter.subtitle')}</p>
        {status === 'success' ? (
          <p className="text-sm text-emerald-400 font-medium">{t('newsletter.success')}</p>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2 max-w-md mx-auto">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('newsletter.email_placeholder')} className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white outline-none placeholder-slate-500" data-testid="newsletter-email" required />
            <button type="submit" disabled={sending} className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium disabled:opacity-50" data-testid="newsletter-submit-btn">
              {sending ? '...' : t('newsletter.subscribe')}
            </button>
          </form>
        )}
        {status === 'error' && <p className="text-sm text-red-400 mt-2">{t('newsletter.error')}</p>}
        <p className="text-[10px] text-gray-600 mt-2">{t('newsletter.privacy_note')}</p>
      </div>
    </div>
  );
}

export default function PublicLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, getAccessTokenSilently } = useAuth0();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('emina_theme') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'));
  const [branding, setBranding] = useState({ header_logo_url: '/logo.png', footer_logo_url: '/logo.png' });
  useA11yInit();

  useEffect(() => {
    fetch(`${API}/public/homepage-settings`).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.branding) setBranding(prev => ({ ...prev, ...d.branding }));
    }).catch(() => {});
  }, []);

  // ── Admin detection via /api/me ────────────────────────────────────────
  // When a user is logged in through Auth0 (e.g. Google social login) we
  // must call the backend to resolve their is_admin flag — Auth0 access
  // tokens don't carry the custom claim by default. `/api/me` upserts the
  // user and returns {is_admin}. For admins we:
  //   1) surface an "Admin Panel" link in the dropdown
  //   2) perform a ONE-TIME auto-redirect to /admin on first login after a
  //      fresh auth session (guarded by sessionStorage so subsequent clicks
  //      on logo/home don't trap the admin in the admin area).
  useEffect(() => {
    let cancelled = false;
    async function resolveAdmin() {
      if (!isAuthenticated) { setIsAdmin(false); return; }
      try {
        const token = await getAccessTokenSilently();
        const r = await fetch(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const adminFlag = !!(d?.is_admin || d?.user?.is_admin);
        setIsAdmin(adminFlag);
        if (adminFlag) {
          const didRedirect = sessionStorage.getItem('emina_admin_auto_redirect');
          const onAdmin = window.location.pathname.startsWith('/admin');
          if (!didRedirect && !onAdmin) {
            sessionStorage.setItem('emina_admin_auto_redirect', '1');
            navigate('/admin');
          }
        }
      } catch { /* silently ignore — Auth0 token errors shouldn't break layout */ }
    }
    resolveAdmin();
    return () => { cancelled = true; };
  }, [isAuthenticated, getAccessTokenSilently, navigate]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('emina_theme', theme);
  }, [theme]);

  useEffect(() => {
    fetch(`${API}/public/categories?lang=${i18n.language}`)
      .then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {});
  }, [i18n.language]);

  function changeLang(code) { i18n.changeLanguage(code); localStorage.setItem('emina_locale', code); setShowLangMenu(false); }
  function doSearch(e) { e.preventDefault(); if (searchQ.trim()) { navigate(`/pretraga?q=${encodeURIComponent(searchQ.trim())}`); setShowSearch(false); setSearchQ(''); } }

  const currentFlag = LANGS.find(l => l.code === i18n.language)?.flag || '🇭🇷';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1220] text-gray-900 dark:text-gray-100 flex flex-col" data-testid="public-layout">
      {/* Breaking ticker */}
      <BreakingTicker />
      {/* Header */}
      <header className="bg-white dark:bg-[#0b1220] border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4">
          <nav className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="shrink-0 mr-4" data-testid="site-logo">
              <img src={branding.header_logo_url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.header_logo_url}` : (branding.header_logo_url || '/logo.png')} alt="e-mina.eu" className="h-10" />
            </Link>

            {/* Desktop nav */}
            <DesktopNav />

            {/* Right icons — balanced spacing between 3-dots (DesktopNav) and lang switcher */}
            <div className="flex items-center gap-1.5 shrink-0 pl-2 border-l border-gray-100 dark:border-gray-800 ml-1.5">
              {/* Admin quick-edit (only visible to logged-in admins, on any public page) */}
              {isAdmin && <AdminEditShortcut />}
              <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-1.5 text-gray-400 hover:text-amber-500 dark:hover:text-amber-300 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" data-testid="theme-toggle" title={theme === 'dark' ? 'Svijetla tema' : 'Tamna tema'}>
                {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
              </button>
              <Link to="/spremljeno" className="hidden md:inline-flex p-1.5 text-gray-400 hover:text-amber-500 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Spremljeno" title="Spremljeno" data-testid="header-saved-link"><Bookmark size={17} /></Link>
              <button onClick={() => setShowSearch(!showSearch)} className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" data-testid="search-toggle" title={t('search.title')}><Search size={17} /></button>
              {/* Accessibility & Bug Report — desktop only, moved to burger on mobile */}
              <div className="hidden md:inline-flex items-center gap-1">
                <AccessibilityWidget />
                <BugReportButton />
              </div>
              {/* Login / User */}
              {isAuthenticated ? (
                <div className="relative">
                  <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-1.5 px-2 py-1 text-xs" data-testid="user-menu-btn">
                    {user?.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <User size={16} className="text-gray-500" />}
                    <span className="hidden sm:inline text-gray-700 dark:text-gray-300 text-xs font-medium">{user?.name?.split(' ')[0]}</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-1 w-44 z-50" data-testid="user-dropdown">
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-b border-gray-100 dark:border-gray-800"
                              data-testid="user-menu-admin-panel">
                          <Shield size={14} /> Admin Panel
                        </Link>
                      )}
                      <Link to="/pretplata" onClick={() => setShowUserMenu(false)} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">Moja pretplata</Link>
                      <Link to="/moji-oglasi" onClick={() => setShowUserMenu(false)} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800" data-testid="user-menu-my-listings">Moji oglasi</Link>
                      <Link to="/spremljeno" onClick={() => setShowUserMenu(false)} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800" data-testid="user-menu-saved">Spremljeni članci</Link>
                      <button onClick={() => { setShowUserMenu(false); try { sessionStorage.removeItem('emina_admin_auto_redirect'); } catch {}; logout({ logoutParams: { returnTo: window.location.origin } }); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"><LogOut size={14} /> Odjava</button>
                    </div>
                  )}
                </div>
              ) : (
                <button type="button" onClick={() => navigate('/prijava')} className="px-3 py-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 hover:text-red-600" data-testid="login-btn">{t('nav.login').toUpperCase()}</button>
              )}
              <div className="relative">
                <button onClick={() => setShowLangMenu(!showLangMenu)} className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900" data-testid="lang-switcher">
                  <span className="text-base">{currentFlag}</span> {i18n.language.toUpperCase()} <ChevronDown size={10} />
                </button>
                {showLangMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white dark:bg-[#0b1220] border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl py-1 w-44 z-50" data-testid="lang-menu">
                    {LANGS.map(l => (
                      <button key={l.code} onClick={() => changeLang(l.code)} className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${i18n.language === l.code ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                        {l.flag} {l.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setMobileMenu(!mobileMenu)} className="xl:hidden p-2 text-gray-500"><Menu size={22} /></button>
            </div>
          </nav>

          {/* Search overlay */}
          {showSearch && (
            <form onSubmit={doSearch} className="pb-3 flex gap-2" data-testid="search-form">
              <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder={t('search.placeholder')} autoFocus className="flex-1 bg-gray-50 dark:bg-[#0b1220] border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400" data-testid="search-input" />
              <button type="submit" className="px-5 py-2.5 bg-red-600 text-white text-sm rounded-lg font-medium">{t('nav.search')}</button>
              <button type="button" onClick={() => setShowSearch(false)} className="p-2 text-gray-400"><X size={18} /></button>
            </form>
          )}

          {/* Mobile menu */}
          {mobileMenu && (
            <div className="xl:hidden pb-4 border-t border-gray-100 dark:border-gray-800 pt-3 space-y-1">
              {NAV_ITEMS.map(item => item.dropdown ? (
                <div key={item.key}>
                  <p className="px-3 py-2 text-xs font-bold text-gray-500 uppercase">{t(item.labelKey)}</p>
                  {item.dropdown.map(sub => (
                    <Link key={sub.to} to={sub.to} onClick={() => setMobileMenu(false)} className="block px-6 py-2 text-sm text-gray-700">{t(sub.labelKey)}</Link>
                  ))}
                </div>
              ) : (
                <Link key={item.key} to={item.to} onClick={() => setMobileMenu(false)} className="block px-3 py-2 text-sm font-bold text-gray-700">{t(item.labelKey)}</Link>
              ))}
              {/* Mobile-only quick tools (moved from header) */}
              <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800 flex items-center gap-2 px-3">
                <Link to="/spremljeno" onClick={() => setMobileMenu(false)}
                  className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-md"
                  data-testid="mobile-menu-saved">
                  <Bookmark size={14} /> {t('nav.saved') !== 'nav.saved' ? t('nav.saved') : 'Spremljeno'}
                </Link>
                <AccessibilityWidget />
                <BugReportButton />
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1"><Outlet /></main>

      {/* Footer */}
      <footer className="bg-slate-950 text-gray-400 mt-12">
        {/* Partners */}
        <FooterPartners />
        {/* Newsletter */}
        <NewsletterWidget />
        <div className="max-w-7xl mx-auto px-4 py-10">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <img src={branding.footer_logo_url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.footer_logo_url}` : (branding.footer_logo_url || '/logo.png')} alt="e-mina.eu" className="h-10 mb-3" data-testid="footer-logo" />
              <p className="text-sm">{t('footer.portal')}</p>
              <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-testid="footer-tagline">{t('footer.tagline')}</p>
            </div>
            <div data-testid="footer-col-categories">
              <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">{t('footer.categories')}</h4>
              <div className="space-y-1.5">
                {categories.slice(0, 6).map(c => {
                  const locName = (c.name_translations && c.name_translations[i18n.language]) || c.name_hr;
                  return (
                    <Link key={c.id} to={`/kategorija/${c.slug}`} className="block text-sm hover:text-white transition-colors">{locName}</Link>
                  );
                })}
              </div>
            </div>
            <div data-testid="footer-col-servisi">
              <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">{t('footer.servisi')}</h4>
              <div className="space-y-1.5 text-sm">
                <Link to="/oglasnik" className="block hover:text-white">{t('footer.srv_oglasnik')}</Link>
                <Link to="/posao" className="block hover:text-white">{t('footer.srv_posao')}</Link>
                <Link to="/kvizovi" className="block hover:text-white">{t('footer.srv_kvizovi')}</Link>
                <Link to="/kripto" className="block hover:text-white">{t('footer.srv_kripto')}</Link>
                <Link to="/vrijeme" className="block hover:text-white">{t('footer.srv_weather')}</Link>
                <Link to="/mapa" className="block hover:text-white">{t('footer.srv_mapa')}</Link>
                <Link to="/predaj-vijest" className="block hover:text-white">{t('footer.srv_submit')}</Link>
                <Link to="/oglasavanje" className="block hover:text-white">{t('footer.srv_advertising')}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">{t('footer.information')}</h4>
              <div className="space-y-1.5 text-sm">
                <Link to="/o-nama" className="block hover:text-white">{t('footer.about')}</Link>
                <Link to="/impressum" className="block hover:text-white">{t('footer.impressum')}</Link>
                <Link to="/politika-privatnosti" className="block hover:text-white">{t('footer.privacy_policy')}</Link>
                <Link to="/postavke-kolacica" className="block hover:text-white">{t('footer.cookies')}</Link>
                <Link to="/pravila-komentiranja" className="block hover:text-white">{t('footer.comment_rules')}</Link>
                <Link to="/pravila-prenosenja-sadrzaja" className="block hover:text-white">{t('footer.content_rules')}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-3 uppercase tracking-wider">{t('footer.contact')}</h4>
              <div className="space-y-1.5 text-sm">
                <Link to="/kontakt" className="block hover:text-white">{t('contact.title')}</Link>
                <Link to="/oglasavanje" className="block hover:text-white">{t('advertising.title')}</Link>
                <Link to="/pretplata" className="block hover:text-white">{t('nav.subscribe')}</Link>
                <p className="pt-1 text-gray-500">info@e-mina.eu</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-6 text-center text-xs">&copy; {new Date().getFullYear()} e-mina.eu. {t('footer.rights')}.</div>
        </div>
        <FooterAttributionBar />
      </footer>
      <CookieBanner />
      <EminaAsistent />
      <BackToTop />
      <PushSubscribePrompt />
      <ScrollHeatmapCollector />
      <InterestsOnboardingModal />
      {showLangMenu && <div className="fixed inset-0 z-30" onClick={() => setShowLangMenu(false)} />}
    </div>
  );
}
